import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';

import { tpslModalOuterSegmentBunja } from './segment';
import { tpslModalBunja } from './state';
import { tpslModalInputBunja } from './tpsl-form-content/state';

const SegmentedControl = createSegmentedControl<'entire' | 'partial'>();

interface Props {
  onChange: (tab: 'entire' | 'partial') => void;
}

export const TpslTab = ({ onChange }: Props) => {
  const { tpslTabAtom, entireOrdersCountAtom, partialOrdersCountAtom } = useBunja(tpslModalBunja);
  const { trackTpslSettingTabClick, trackTpslSettingInfoClick } = useBunja(tpslModalOuterSegmentBunja);
  const { resetPrices } = useBunja(tpslModalInputBunja);
  const [tpslTab, setTpslTab] = useAtom(tpslTabAtom);
  const entireOrdersCount = useAtomValue(entireOrdersCountAtom);
  const partialOrdersCount = useAtomValue(partialOrdersCountAtom);
  const { isMobile } = useBreakpoint();

  return (
    <div className={css({ pos: 'relative', w: '100%' })}>
      <SegmentedControl
        value={tpslTab}
        onChange={(tab) => {
          trackTpslSettingTabClick(tab);
          setTpslTab(tab);
          resetPrices();
          onChange?.(tab);
        }}
        size='medium'
        css={{ w: isMobile ? 248 : 380, mx: 'auto' }}
      >
        <SegmentedControl.Item value='entire'>
          <T id='Trade.r260330.Entire' />
          {entireOrdersCount > 0 && (
            <span>
              {' '}
              (<Formatted.Number value={entireOrdersCount} />)
            </span>
          )}
        </SegmentedControl.Item>
        <SegmentedControl.Item value='partial'>
          <T id='Trade.r260330.Partial' />
          {partialOrdersCount > 0 && (
            <span>
              {' '}
              (<Formatted.Number value={partialOrdersCount} />)
            </span>
          )}
        </SegmentedControl.Item>
      </SegmentedControl>
      <div className={css({ pos: 'absolute', right: 0, top: 6 })}>
        <Tooltip.Root placement='bottom-end' maxWidth={320}>
          <Tooltip.Trigger>
            <div
              className={center({ w: 32, h: 32, color: 'content.default.level-3' })}
              onClick={trackTpslSettingInfoClick}
            >
              <InformationCircleIcon size={20} />
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <div className={stack({ gap: 12, textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <div className={stack({ gap: 4 })}>
                <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
                  <T id='Trade.r260330.Entire_positions' />
                </p>
                <p>
                  <T id='Trade.r260330.Entire_tp_sl_description' />
                </p>
              </div>
              <div className={stack({ gap: 4 })}>
                <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
                  <T id='Trade.r260330.Partial_positions' />
                </p>
                <p>
                  <T id='Trade.r260330.Partial_tp_sl_description' />
                </p>
              </div>
            </div>
          </Tooltip.Content>
        </Tooltip.Root>
      </div>
    </div>
  );
};
